import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Cloud, RadioTower, Satellite } from "lucide-react";
import { C, Icon, Label, Packet, Pop, Svg, Unit, useTick } from "./shared";

const SITES = [
  { id: "a", name: "BRANCH A", x: 95, y: 232, backup: "STARLINK", bIcon: Satellite, bx: 42, by: 122, fibre: "M112,206 L284,76", via: "M72,206 L42,134 L280,58", base: 9 },
  { id: "hq", name: "HQ", x: 300, y: 282, backup: "5G", bIcon: RadioTower, bx: 392, by: 176, fibre: "M300,256 L300,80", via: "M328,256 L392,190 L316,74", base: 6 },
  { id: "b", name: "BRANCH B", x: 505, y: 232, backup: "STARLINK", bIcon: Satellite, bx: 558, by: 122, fibre: "M488,206 L316,76", via: "M528,206 L558,134 L320,58", base: 11 },
];
const MESH = ["M95,232 L300,282", "M300,282 L505,232", "M95,232 Q300,176 505,232"];
const SCRIPT = ["none", "a", "none", "hq", "none", "b"];
const RATE = { a: 25, hq: 45, b: 25 };
const rev = (d) => d.replace(/^M/, "").split(" L").reverse().join(" L").replace(/^/, "M");

const CAPTION = {
  none: "All three sites are online over fibre and securely meshed together with Site Magic.",
  a: "Branch A's fibre just went down — Starlink took over in under a second, saving about £25 every minute it's out.",
  hq: "HQ lost its fibre — the 5G backup kicked in, calls stayed connected and roughly £45 a minute kept flowing.",
  b: "Branch B's line dropped — traffic moved to Starlink and the site-to-site VPN stayed up.",
  multi: "Several sites are on Starlink / 5G backup right now — every office is still online.",
};

const Site = ({ s, down, ms }) => (
  <g data-testid={`sdwan-site-${s.id}`}>
    <rect x={s.x - 55} y={s.y - 26} width="110" height="54" rx="4" fill="#0D131E" stroke={down ? C.green : "#334155"} strokeOpacity={down ? 0.7 : 1} />
    <Unit x={s.x - 42} y={s.y - 16} w={84} h={16} ports={4} sfp={1} />
    <Label x={s.x} y={s.y + 20} color={C.text} size={9}>{s.name}</Label>
    <Label x={s.x} y={s.y + 44} size={8} color={down ? C.green : C.cyan}>{down ? `${s.backup} · ${ms}ms` : `FIBRE · ${ms}ms`}</Label>
  </g>
);

const Uplinks = ({ s, down }) => (
  <g>
    <path d={s.fibre} fill="none" stroke={down ? C.red : C.cyan} strokeOpacity="0.75" strokeWidth="1.3" strokeDasharray={down ? "3 5" : undefined} />
    {down ? <Label x={(s.x + 300) / 2 + (s.id === "hq" ? 16 : 0)} y={s.id === "hq" ? 170 : 150} size={10} color={C.red}>✕</Label> : <Packet d={rev(s.fibre)} dur={1.8} begin={s.base / 10} />}
    <path d={s.via} fill="none" stroke={down ? C.green : "#475569"} strokeWidth={down ? 1.6 : 1} strokeDasharray="4 6" className={down ? "flow flow-in" : ""} />
    {down && <Packet d={rev(s.via)} dur={1.6} color={C.green} />}
    <circle cx={s.bx} cy={s.by} r="14" fill={C.body} stroke={down ? C.green : "#475569"} />
    <Icon as={s.bIcon} x={s.bx} y={s.by} s={14} color={down ? C.green : C.dim} />
    <Label x={s.bx} y={s.by - 20} size={7} color={down ? C.green : C.dim}>{s.backup}{down ? " ACTIVE" : ""}</Label>
  </g>
);

const Controls = ({ down, auto, toggle, setAuto }) => (
  <div className="flex flex-wrap items-center gap-2 font-mono text-[9px] sm:text-[10px] tracking-[0.18em]" data-testid="sdwan-controls">
    <span className="text-slate-500 mr-1">CUT FIBRE →</span>
    {SITES.map((s) => (
      <button key={s.id} type="button" onClick={() => toggle(s.id)} data-testid={`sdwan-cut-${s.id}`} className={`rounded-full border px-3 py-1.5 transition-colors ${down[s.id] ? "border-rose-400/60 bg-rose-500/10 text-rose-300" : "border-white/15 text-slate-300 hover:border-cyan-300/60"}`}>
        {s.name}{down[s.id] ? " ✕" : ""}
      </button>
    ))}
    <button type="button" onClick={() => setAuto(true)} data-testid="sdwan-auto" className={`ml-auto rounded-full border px-3 py-1.5 transition-colors ${auto ? "border-cyan-300/60 text-cyan-300" : "border-white/15 text-slate-400 hover:text-white"}`}>
      {auto ? "● AUTO DEMO" : "RESUME DEMO"}
    </button>
  </div>
);

export const MultiSiteAnim = ({ say, slot }) => {
  const [down, setDown] = useState({ a: false, hq: false, b: false });
  const [auto, setAuto] = useState(true);
  const [jitter, setJitter] = useState(0);
  const [saved, setSaved] = useState(0);
  const step = useRef(0);
  const downRef = useRef(down);
  downRef.current = down;

  useTick(() => {
    const add = Object.keys(RATE).reduce((a, k) => a + (downRef.current[k] ? RATE[k] / 5 : 0), 0);
    if (add) setSaved((v) => v + add);
  }, 200);

  useTick(() => {
    setJitter(Math.random());
    if (!auto) return;
    step.current = (step.current + 1) % (SCRIPT.length * 4);
    if (step.current % 4) return;
    const cur = SCRIPT[step.current / 4];
    setDown({ a: cur === "a", hq: cur === "hq", b: cur === "b" });
  }, 1000);

  const toggle = (id) => {
    setDown((d) => (auto ? { a: false, hq: false, b: false, [id]: true } : { ...d, [id]: !d[id] }));
    setAuto(false);
  };

  const failed = SITES.filter((s) => down[s.id]);
  useEffect(() => {
    say(failed.length > 1 ? CAPTION.multi : CAPTION[failed[0]?.id || "none"]);
  }, [down, say]); // eslint-disable-line react-hooks/exhaustive-deps

  const ms = (s) => (down[s.id] ? (s.backup === "5G" ? 32 : 38) : s.base) + Math.round(jitter * 4);

  return (
    <>
      <Svg>
        <Pop delay={0.1}>
          <circle cx="300" cy="54" r="26" fill={C.body} stroke={C.line} />
          <Icon as={Cloud} x={300} y={54} s={22} color={C.cyan} />
        </Pop>
        <Label x={300} y={98} size={8}>INTERNET · AZURE</Label>
        {SITES.map((s) => <Uplinks key={s.id} s={s} down={down[s.id]} />)}
        {MESH.map((d, i) => (
          <g key={i}>
            <path d={d} stroke={C.green} strokeOpacity="0.5" strokeDasharray="4 6" className="flow" fill="none" />
            <Packet d={d} dur={2.4} begin={i * 0.6} color={C.green} r={2.4} />
          </g>
        ))}
        <Label x={300} y={216} size={8} color={C.green}>SITE MAGIC · MESH VPN</Label>
        {SITES.map((s, i) => <Pop key={s.id} delay={0.3 + i * 0.15}><Site s={s} down={down[s.id]} ms={ms(s)} /></Pop>)}
        <rect x="16" y="16" width="200" height="24" rx="12" fill={failed.length ? "rgba(16,185,129,0.12)" : "rgba(0,240,255,0.06)"} stroke={failed.length ? C.green : C.line} />
        <Label x={116} y={32} size={8} color={failed.length ? C.green : C.text} data-testid="sdwan-status">
          {failed.length ? `3/3 ONLINE · ${failed.length} ON BACKUP` : "3/3 SITES ONLINE · FIBRE"}
        </Label>
        {failed.length > 0 && <Label x={584} y={32} anchor="end" size={8} color={C.amber}>VOIP & TEAMS PRIORITISED</Label>}
        <g data-testid="sdwan-downtime-counter">
          <rect x="398" y="336" width="186" height="50" rx="6" fill={failed.length ? "rgba(16,185,129,0.1)" : "rgba(11,15,23,0.85)"} stroke={failed.length ? C.green : "#1F2937"} />
          <Label x={410} y={352} anchor="start" size={7} color={failed.length ? C.green : C.dim}>DOWNTIME COST AVOIDED</Label>
          <Label x={410} y={376} anchor="start" color={failed.length ? "#34D399" : C.text} family="Outfit, sans-serif" spacing="0" weight="800" size={20} data-testid="sdwan-downtime-value">{`£${Math.round(saved).toLocaleString("en-GB")}`}</Label>
          {failed.length > 0 && <Label x={574} y={376} anchor="end" size={7} color={C.green}>+£{failed.reduce((a, s) => a + RATE[s.id], 0)}/MIN</Label>}
        </g>
      </Svg>
      {slot && createPortal(<Controls down={down} auto={auto} toggle={toggle} setAuto={setAuto} />, slot)}
    </>
  );
};
